import React, { useState, useContext, Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardBody, Table } from 'reactstrap';
import {
  FlowContainer,
  OperFlowService,
  FlowType,
  FlowSPRData,
  FlowSprMission,
  FlowSprDoc,
} from '../../../../services/types/Flow';
import {
  Button,
  ConfirmModal,
  CustomModal,
  SpinnerArea,
  Modal,
} from '../../../../shared/components';
import FlowVersionsTable from './FlowVersionsTable';
import { User } from '../../../../services/types/User';
import Context from '../../../App/MainProvider/context';
import useServiceReducer from '../../../../shared/hooks/useServiceReducer';
import reducer, { SearchUserAction } from '../UserQueryReducer';
import userService from '../../../../services/userService';
import AutocompleteChips from '../../../../shared/components/AutocompleteChips';
import FlowFeedbackProvider from './FlowFeedbackProvider';
import FlowFeedbackTable from './FlowFeedbackTable';
import { BasicNotification } from '../../../../shared/components/Notification';
import { safeFind, dateTimeFormat, customDateStr, isDraft } from '../../../../shared/misc/utils';
import OperationContext from '../context';
import InviteReviewersForm, { InviteReviewersFormData } from './InviteReviewersForm';
import SprForm, { SprFormData } from './Spr/SprForm';
import FlowDocuments from './Documents';
import FlowCurrentVersionsTable from './FlowCurrentVersionsTable';
import FlowRelatedSelection from './FlowRelatedSelection';
import FlowCycle2 from './FlowCycle2';
import Role from '../../../../types/role';
import ReviewersTable from './ReviewersTable';
import operEsapService from '../../../../services/operEsapService';
import { shouldKeepSendForReview } from './flowFeedbackRules';

const CHEVRON_UP = 'chevron-up';
const CHEVRON_DOWN = 'chevron-down';

interface Props {
  data: FlowContainer;
  reload: () => void;
  operFlowService: OperFlowService;
  flowType: FlowType;
  flowTypeName?: string;
}

const FlowMain: React.FC<Props> = (props) => {
  const { data, reload, operFlowService, flowType, flowTypeName = flowType } = props;
  const { t } = useTranslation();
  const { service, notify, user, inSuperUserMode } = useContext(Context);
  const { operation, reload: reloadOperation } = useContext(OperationContext);

  const [members, loadingMembers, , dispatch] = useServiceReducer(userService.getUsers, reducer, {
    query: '',
    convergence: true,
    includeGS: false,
  });

  const [showingVersions, setShowingVersions] = useState(false);
  const [showingSupportDocuments, setShowingSupportDocuments] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showRestartModal, setShowRestartModal] = useState(false);
  const [showNextVersionModal, setShowNextVersionModal] = useState(false);
  const [showInviteReviewersModal, setShowInviteReviewersModal] = useState(false);
  const [showPendingFeedbackModal, setShowPendingFeedbackModal] = useState(false);
  const [showSprModal, setShowSprModal] = useState(false);
  const [showFinalReviewModal, setShowFinalReviewModal] = useState(false);
  const [feedback, setFeedback] = useState({
    indicationCode: data.indicators[0],
    comment: data.userPendingFeedback ? data.userPendingFeedback.comment || '' : '',
  });
  const [showReviewersModal, setShowReviewersModal] = useState(false);
  const [showingAddReviewers, setShowingAddReviewers] = useState(false);
  const [nextReviewState, setNextReviewState] = useState<{ members: string[] }>({ members: [] });
  const [formData, setFormData] = useState<FormData | null>(null);

  const [loading, setLoading] = useState(false);

  const [finalEsrs, setFinalEsrs] = useState(false);

  const [relatedIds, setRelatedIds] = useState<number[]>([]);

  const [docSprRationale, setDocSprRationale] = useState<FlowSprDoc | null>(null);
  const [SprMissions, setSprMissions] = useState([] as FlowSprMission[]);
  const [membersForAdd, setMembersForAdd] = useState([] as User[]);
  const [showEsapV1NoContentModal, setShowEsapV1NoContentModal] = useState(false);
  const [showEsapV1ContentModal, setShowEsapV1ContentModal] = useState(false);
  const [showNoEsapPreviousEsrsContentModal, setShowNoEsapPreviousEsrsContentModal] =
    useState(false);
  const [showNoEsapPreviousEsrsNoContentModal, setShowNoEsapPreviousEsrsNoContentModal] =
    useState(false);
  const [showEsapPreviousEsrsNoModified, setShowEsapPreviousEsrsNoModified] = useState(false);
  const [showEsapPreviousEsrsModified, setShowEsapPreviousEsrsModified] = useState(false);
  const [showEsapPreviousEsrsNoContent, setShowEsapPreviousEsrsNoContent] = useState(false);

  const [showEsapPreviousSRModified, setShowEsapPreviousSRModified] = useState(false);
  const [showEsapPreviousSRNoModified, setShowEsapPreviousSRNoModified] = useState(false);
  const [showEsapPreviousSRDeleted, setShowEsapPreviousSRDeleted] = useState(false);
  const [showNoEsapPreviousSRContentModal, setShowNoEsapPreviousSRContentModal] = useState(false);
  const [showNoEsapPreviousSRNoContentModal, setShowNoEsapPreviousSRNoContentModal] =
    useState(false);

  useEffect(() => {
    if (
      data &&
      data.actions.includes('FEEDBACK') &&
      (data.actions.includes('NEXTSTEP') || data.actions.includes('APPROVE'))
    ) {
      setFeedback({ ...feedback, indicationCode: '' });
    } else {
      setFeedback({ ...feedback, indicationCode: data.indicators[0] });
    }
  }, [data.currentStep]);

  const chevron = showingVersions ? CHEVRON_UP : CHEVRON_DOWN;

  const chevronReviewers = showingAddReviewers ? CHEVRON_UP : CHEVRON_DOWN;

  const currentVersion = data.content && data.content[0];
  const spr = data.currentSPR;

  const showBtnReviewers = data.steps[data.currentStep - 1].name !== 'Approved';

  const previousVersions =
    data.content && currentVersion ? data.content.filter((x) => x.id !== currentVersion.id) : null;

  const isFinalReview = !!(currentVersion && currentVersion.step === data.steps.length - 1);

  const isSR = data.type === 'SR' || data.type === 'SR_ESPF';
  const allowDoubleFeedback =
    (data.isGhPrimary || user.isSuperUser) && isSR && data.currentStep === 2;

  const toRestartConfirm = () => {
    setShowRestartModal(true);
  };
  const restart = () => {
    const dto = {
      operationId: operation.id,
      flowId: data.id,
      flowLap: data.currentLap,
      language: data.language,
      relatedOperationIds: relatedIds,
    };
    service(() => operFlowService.restart(dto), {
      callback: () => {
        setShowRestartModal(false);
        notify(flowTypeName, t('successFlowRestart', { flowTypeName }), 'success');
        reload();
        setShowingSupportDocuments(false);
      },
      loader: setLoading,
    });
  };

  const relate = () => {
    const dto = {
      operationId: operation.id,
      relatedOperationIds: relatedIds,
    };
    service(() => operFlowService.relate(dto), {
      callback: () => {
        notify(flowTypeName, t('successFlowRelate'), 'success');
        setRelatedIds([]);
        reload();
      },
      loader: setLoading,
    });
  };

  const maxRelated = () =>
    (flowType === 'INITIAL_ESRS' || flowType === 'ESRS' || flowType === 'SR_ESPF') &&
    relatedIds.length > 4;

  const sendForNextReview = (sPRData?: FlowSPRData, createEsap: boolean | null = null) => {
    service(
      () =>
        operFlowService.sendForNextReview({
          operationFlowId: data.id,
          members: nextReviewState.members,
          fromStep: data.currentStep,
          sPRData,
          comment: feedback.comment || null,
          createEsap,
        }),
      {
        callback: () => {
          setShowNextVersionModal(false);
          setFeedback({
            indicationCode: '',
            comment: data.userPendingFeedback ? data.userPendingFeedback.comment || '' : '',
          });
          notify(flowTypeName, t('successFlowNextReview'), 'success');
          reload();
        },
        loader: setLoading,
      }
    );
  };

  const handleMembersSearch = (egsMemberQuery: string) => {
    const action = {
      type: 'QUERY_USER',
      query: egsMemberQuery,
      convergence: false,
    } as SearchUserAction;

    dispatch(action);
  };

  const inviteReviewers = (invitedMembers: string[]) => {
    if (invitedMembers.length > 0) {
      service(
        () =>
          operFlowService.inviteReviewers({
            operationFlowId: data.id,
            members: invitedMembers,
          }),
        {
          callback: () => {
            setShowInviteReviewersModal(false);
            notify(flowTypeName, t('Successfully Invited Reviewers'), 'success');
            reload();
            setMembersForAdd([]);
          },
          loader: setLoading,
        }
      );
    }
  };

  const getMessage = () => {
    switch (feedback.indicationCode) {
      case 'APPROVE':
        return t('successFlowApproved');
      case 'FEEDBACK':
        return t('successFlowProvideFeedback');
      case 'SEND':
        return isFinalReview ? t('successFlowApproved') : t('successFlowSent');
      default:
        return t('successSave');
    }
  };

  const provideFeedback = () => {
    if (!feedback.indicationCode) {
      notify(flowTypeName, 'Please select a Feedback Indication', 'danger', 'exclamation-triangle');
    } else {
      service(
        () =>
          operFlowService.provideFeedback({
            operationFlowId: data.id,
            fromStep: data.currentStep,
            members: nextReviewState.members,
            ...feedback,
          }),
        {
          callback: () => {
            setShowNextVersionModal(false);
            notify(flowTypeName, getMessage(), 'success');
            setFeedback({
              indicationCode: '',
              comment: '',
            });
            reload();
            if (
              (isFinalReview && feedback.indicationCode !== data.indicators[0]) ||
              allowDoubleFeedback
            ) {
              // is approving
              reloadOperation();
            }
          },
          loader: setLoading,
        }
      );
    }
  };

  const saveFeedback = (feedbackForSave: string) => {
    service(
      () =>
        operFlowService.saveFeedback({
          operationFlowId: data.id,
          comment: feedbackForSave,
        }),
      {
        callback: () => {
          notify(flowTypeName, t('successFlowSaveFeedback'), 'success');
        },
        loader: setLoading,
      }
    );
  };

  const downloadDoc = (versionId: number) => {
    service(() => operFlowService.downloadDocVersion(versionId), {
      loader: setLoading,
    });
  };

  const downloadEsap = (versionId: number) => {
    service(() => operEsapService.downloadDocVersion(versionId), {
      loader: setLoading,
    });
  };

  // const downloadEsap = (esapId: number) => {
  //   service(() => operFlowService.downloadEsap(esapId), {
  //     loader: setLoading
  //   });
  // };

  const toUploadConfirm = (fData: FormData) => {
    fData.append('parentId', data.id.toString());
    setFormData(fData);
    setShowUploadModal(true);
  };
  const upload = () => {
    if (!formData) {
      return;
    }
    service(() => operFlowService.upload(formData), {
      callback: (result) => {
        if (result) {
          reload();
        }
      },
      loader: setLoading,
    });
  };

  const handleMembersChange = (selected: User[]) => {
    const formattedEgsMembers = selected.map((u) => u.email);
    setNextReviewState({ members: formattedEgsMembers });
  };

  const applyComplianceChanges = () => {
    service(() => operFlowService.applyComplianceChanges(data.id), {
      callback: () => {
        notify(flowTypeName, t('successChangesApplied'), 'success');
        reload();
      },
      loader: setLoading,
    });
  };

  const discardComplianceChanges = () => {
    service(() => operFlowService.discardComplianceChanges(data.id), {
      callback: () => {
        notify(flowTypeName, t('successChangesDiscarded'), 'success');
        reload();
      },
      loader: setLoading,
    });
  };

  const applyActionPlanChanges = () => {
    service(() => operFlowService.applyActionPlanChanges(data.id), {
      callback: () => {
        notify(flowTypeName, t('successChangesApplied'), 'success');
        reload();
      },
      loader: setLoading,
    });
  };

  const discardActionPlanChanges = () => {
    service(() => operFlowService.discardActionPlanChanges(data.id), {
      callback: () => {
        notify(flowTypeName, t('successChangesDiscarded'), 'success');
        reload();
      },
      loader: setLoading,
    });
  };

  const applyIndicatorsChanges = () => {
    service(() => operFlowService.applyIndicatorsChanges(data.id), {
      callback: () => {
        notify(flowTypeName, t('successChangesApplied'), 'success');
        reload();
      },
      loader: setLoading,
    });
  };

  const discardIndicatorshanges = () => {
    service(() => operFlowService.discardIndicatorsChanges(data.id), {
      callback: () => {
        notify(flowTypeName, t('successChangesDiscarded'), 'success');
        reload();
      },
      loader: setLoading,
    });
  };

  const openWord = () => {
    service(() => operFlowService.openWord(operation.id), {
      callback: (wordLink: string) => {
        window.open(wordLink);
      },
      loader: setLoading,
    });
  };

  const checkEsap = () => {
    service(() => operFlowService.checkEsap(data.id), {
      callback: (resultEsap) => {
        setShowNextVersionModal(false);
        if (data.currentLap === 1) {
          // version 1
          if (resultEsap.newHasContent) {
            setShowEsapV1ContentModal(true);
          } else {
            setShowEsapV1NoContentModal(true);
          }
        } else if (resultEsap.previousEsap) {
          if (resultEsap.newHasContent) {
            if (resultEsap.newDifferent) {
              setShowEsapPreviousEsrsModified(true);
            } else {
              setShowEsapPreviousEsrsNoModified(true);
            }
          } else {
            setShowEsapPreviousEsrsNoContent(true);
          }
        } else if (resultEsap.newHasContent) {
          setShowNoEsapPreviousEsrsContentModal(true);
        } else {
          setShowNoEsapPreviousEsrsNoContentModal(true);
        }
      },
      loader: setLoading,
    });
  };

  const checkEsapSREspf = () => {
    service(() => operFlowService.checkEsap(data.id), {
      callback: (resultEsap) => {
        setShowNextVersionModal(false);
        if (resultEsap.previousEsap) {
          if (resultEsap.newHasContent) {
            if (resultEsap.newDifferent) {
              setShowEsapPreviousSRModified(true);
            } else {
              setShowEsapPreviousSRNoModified(true);
            }
          } else {
            setShowEsapPreviousSRDeleted(true);
          }
        } else if (resultEsap.newHasContent) {
          setShowNoEsapPreviousSRContentModal(true);
        } else {
          setShowNoEsapPreviousSRNoContentModal(true);
        }
      },
      loader: setLoading,
    });
  };

  const handleAddSRPRationale = async () => {
    await service(() => operFlowService.getSPRRationale(data.id), {
      callback: (rationale) => {
        setDocSprRationale(rationale);
        setShowSprModal(true);
      },
      loader: setLoading,
    });
  };

  const handleGetMissions = async () => {
    await service(() => operFlowService.getSPRMissions(data.id), {
      callback: (missions) => {
        setSprMissions(missions);
      },
      loader: setLoading,
    });
  };

  const handleAddSRPRationaleCheckFlow = () => {
    if (flowType === 'SR_ESPF') {
      checkEsapSREspf();
    } else {
      handleGetMissions();
      handleAddSRPRationale();
    }
  };

  let createEsapRationale = false;

  const loadSPRFinalReviewGH = async () => {
    await service(() => operFlowService.getSPRRationale(data.id), {
      callback: (rationale) => {
        setDocSprRationale(rationale);
      },
      loader: setLoading,
    });
    await service(() => operFlowService.getSPRMissions(data.id), {
      callback: (missions) => {
        setSprMissions(missions);
      },
      loader: setLoading,
    });
  };

  const sendForNextReviewSREspf = (sPRData?: FlowSPRData, createEsap: boolean | null = null) => {
    if (data.mustAddSPR && !spr) {
      if (createEsap) {
        createEsapRationale = createEsap;
      }
      handleGetMissions();
      handleAddSRPRationale();
    } else {
      sendForNextReview(sPRData, createEsap);
    }
  };
  const isDraftOperation = isDraft(operation);
  const isCof = operation.primaryMember && operation.primaryMember.isCof;
  const isFeedbackAndSend = feedback.indicationCode !== data.indicators[0];
  const canEdit = data.actions.includes('EDITDOC') && !isDraftOperation;
  const canFeedback = data.actions.includes('FEEDBACK') && !isDraftOperation;
  const canStepForward = data.actions.includes('NEXTSTEP') && !isDraftOperation;
  const canRestartFlow = data.actions.includes('RESTART') && !isDraftOperation;
  const canApprove = data.actions.includes('APPROVE') && !isDraftOperation;
  const supportDocumentsEditable =
    isSR && (canEdit || operation.isMember || user.isSuperUser) && !isDraftOperation;
  const isFlowApproved = data.currentStep === data.steps.length;
  const isGroupHead = [user.roleCode, user.secondRoleCode].includes(Role.GroupHead);
  const isSRESPFFinalReviewByGH =
    data.type === 'SR_ESPF' && isFinalReview && (isGroupHead || inSuperUserMode());
  const isAuthorizedUser = canEdit || operation.isMember || inSuperUserMode() || isGroupHead;

  const indications = data.indicators.map((option, index) => ({ id: index, name: option }));

  // Al guardar un comentario el backend agrega FEEDBACK a las acciones del paso. Para
  // el especialista que solo puede comentar eso reemplazaba "Send for Review" por
  // "Apply", un boton que exige una indicacion que FlowFeedbackProvider no le muestra.
  // La regla vive en flowFeedbackRules y la usan las dos pantallas: fue justamente
  // tenerla duplicada y desincronizada lo que produjo el defecto.
  const speCanFeedback = data.canFeedback && !canApprove;
  const keepSendForReview = shouldKeepSendForReview({
    canStepForward,
    speCanFeedback,
    inSuperUserMode: inSuperUserMode(),
    currentStep: data.currentStep,
    isStepRevise: data.isStepRevise,
  });

  const buttonText = canFeedback && canStepForward ? t('apply') : t('sendFeedback');

  const hasPendingFeedback = data.otherPendingFeedbacks.length > 0;

  const feedbackActionHandler = async () => {
    if (isSRESPFFinalReviewByGH && feedback.indicationCode === 'SEND') {
      await loadSPRFinalReviewGH();
      setShowFinalReviewModal(true);
      return;
    }

    if (isFeedbackAndSend && feedback.indicationCode !== 'APPROVE') {
      if (hasPendingFeedback) {
        setShowPendingFeedbackModal(true);
      } else if (data.canAddReviewer && (user.roleCode !== Role.Specialist || inSuperUserMode())) {
        setShowNextVersionModal(true);
      } else if (flowType === 'ESRS') {
        setFinalEsrs(true);
        checkEsap();
      } else {
        provideFeedback();
      }
    } else {
      provideFeedback();
    }
  };

  const actions = data.actions.filter(
    (ac) => ac === 'FEEDBACK' || ac === 'NEXTSTEP' || ac === 'RESTART'
  );

  const renderButtonActions = () =>
    actions.map((x, i) => {
      const color = i === 0 && actions.length > 1 ? 'secondary' : 'primary';
      return (
        <Fragment key={`action${i.toString()}`}>
          {x === 'FEEDBACK' && !keepSendForReview && (
            <Button
              key={`feedback_${i.toString()}`}
              className={`btn-${actions.some((ac) => ac === 'NEXTSTEP') ? 'primary' : color}`}
              text={buttonText}
              type="button"
              onClick={feedbackActionHandler}
              // disabled={!data.indicators.includes(feedback.indicationCode)}
            />
          )}
          {x === 'NEXTSTEP' && (!canFeedback || keepSendForReview) && !data.mustAddReviewer && (
            <Button
              key={`next_${i.toString()}`}
              className={`btn-${color}`}
              text={t('sendNextReview')}
              type="button"
              onClick={async () => {
                if (data.mustAddSPR) {
                  if (spr) {
                    await handleGetMissions();
                    await handleAddSRPRationale();
                    setShowSprModal(true);
                  } else {
                    handleAddSRPRationaleCheckFlow();
                  }
                } else if (
                  data.canAddReviewer &&
                  (user.roleCode !== Role.Specialist || inSuperUserMode())
                ) {
                  setShowNextVersionModal(true);
                } else if (flowType === 'ESRS') {
                  checkEsap();
                } else if (flowType === 'SR_ESPF') {
                  checkEsapSREspf();
                } else {
                  sendForNextReview();
                }
              }}
            />
          )}
        </Fragment>
      );
    });

  const renderPendingFeedbacks = () => {
    if (data.otherPendingFeedbacks.length === 0 || !currentVersion) {
      return null;
    }
    return data.otherPendingFeedbacks.map((x, i) => (
      <tr key={`pendingFeedback_tr${i.toString()}`}>
        <td className="text-nowrap">{`${flowTypeName} v.${currentVersion.lap}.${currentVersion.version}`}</td>
        <td className="text-nowrap">{x.user}</td>
        <td>{dateTimeFormat(x.date)}</td>
        <td>{x.comment}</td>
      </tr>
    ));
  };

  const getLastEsap = () => {
    if (data.content && operation.number !== data.ownerOperation.number) {
      return data.esapRelated;
    }

    if (data.content) {
      const esaps = data.content.filter((x) => x.esap).map((x) => x.esap);
      const sortEsaps = esaps.sort((a, b) => {
        if (!a || !b) {
          return -1;
        }
        return b.version - a.version;
      });

      return sortEsaps[0];
    }
    return null;
  };

  const deleteFlow = (deleteType: string) => {
    service(
      () => {
        if (deleteType === 'delete-flow') {
          return operFlowService.deleteFlow(data.id);
        }
        return operFlowService.deleteFlow(data.id, data.currentLap);
      },
      {
        callback: () => {
          notify(flowTypeName, t(`success-${deleteType}`), 'success');
          if (
            flowType === 'SR_ESPF' && data.content ? data.content[0].status === 'APPROVED' : false
          ) {
            notify(
              flowTypeName,
              t(`success-delete-remember-convergence-sr`),
              'warning',
              'exclamation-triangle'
            );
          }
          reload();
        },
        loader: setLoading,
      }
    );
  };

  return (
    <SpinnerArea loading={loading}>
      {data.content && currentVersion && (
        <>
          <hr />
          {currentVersion.source !== 'Legacy_Convergence' && (
            <FlowCycle2
              steps={data.steps}
              currentStep={safeFind(data.steps, (x) => x.id === currentVersion.step).id}
              versions={data.content}
              currentVersion={currentVersion.version}
              currentLap={currentVersion.lap}
              flowId={currentVersion.flowId}
            />
          )}
          <Card className="flow-card">
            <CardBody className="card-body-top">
              <FlowCurrentVersionsTable
                data={data.content[0]}
                openWord={openWord}
                handleDownloadDoc={downloadDoc}
                handleUpload={toUploadConfirm}
                language={data.language}
                flowType={flowType}
                flowTypeName={flowTypeName}
                canEdit={canEdit}
                relatedOperations={data.relatedOperations}
                setRelatedOperationIds={setRelatedIds}
                canRelate={
                  isAuthorizedUser &&
                  operation.number === data.ownerOperation.number &&
                  data.content[0].step === 1
                }
                handleRelate={relate}
                currentRelatedOperationIds={relatedIds}
                operationOwner={data.ownerOperation}
                setShowReviewersModal={setShowReviewersModal}
                showBtnReviewers={showBtnReviewers}
                createEsap={currentVersion.lap === 1 || data.createEsap}
                lastEsap={getLastEsap()}
                hasEsap={data.hasEsap}
                changesEsapContent={data.changesEsapContent}
                hasEsapContent={data.hasEsapContent}
                currentEsapHasContent={data.hasEsapContent}
                deleteAction={deleteFlow}
              />
              {data.complianceCreated && canEdit && (
                <div className="notification-wrapper-100 d-flex justify-content-center mb-3">
                  <BasicNotification
                    title={t('flowComplianceCreatedTitle')}
                    message={t('flowComplianceCreatedMessage', { flowType })}
                    color="warning"
                    icon="exclamation-triangle"
                    buttonAction={{ text: t('yesApplyChanges'), action: applyComplianceChanges }}
                    secondButtonAction={{
                      text: t('discardChanges'),
                      action: discardComplianceChanges,
                    }}
                  />
                </div>
              )}
              {data.actionPlanCreated && canEdit && (
                <div className="notification-wrapper-100 d-flex justify-content-center mb-3">
                  <BasicNotification
                    title={t('flowIndicatorsChangedTitle')}
                    message={t('flowActionPlanCreatedMessage', { flowType })}
                    color="warning"
                    icon="exclamation-triangle"
                    buttonAction={{ text: t('yesApplyChanges'), action: applyActionPlanChanges }}
                    secondButtonAction={{
                      text: t('discardChanges'),
                      action: discardActionPlanChanges,
                    }}
                  />
                </div>
              )}
              {data.indicatorsChanges && canEdit && (
                <div className="notification-wrapper-100 d-flex justify-content-center mb-3">
                  <BasicNotification
                    title={t('flowIndicatorsChangedTitle')}
                    message={t('flowIndicatorsChangedMessage', { flowType })}
                    color="warning"
                    icon="exclamation-triangle"
                    buttonAction={{ text: t('yesApplyChanges'), action: applyIndicatorsChanges }}
                    secondButtonAction={{
                      text: t('discardChanges'),
                      action: discardIndicatorshanges,
                    }}
                  />
                </div>
              )}
              {data.mustAddReviewer && canEdit && !canFeedback && (
                <div className="notification-wrapper-100 d-flex justify-content-center mb-3">
                  <BasicNotification
                    title={t('mustAddReviewersWarningTitle')}
                    message={t('mustAddReviewersWarningBody')}
                    color="warning"
                    icon="exclamation-triangle"
                  />
                </div>
              )}
              {isSR && (
                <div className="d-block clearfix">
                  <Button
                    className="btn-tertiary pl-1 btn-aide-memoir-support-docs"
                    iconRight={showingSupportDocuments ? CHEVRON_UP : CHEVRON_DOWN}
                    text={t('supportDocumentsAideMemoire')}
                    type="button"
                    onClick={() => setShowingSupportDocuments((current) => !current)}
                  />
                  {showingSupportDocuments && (
                    <FlowDocuments
                      flowId={data.id}
                      flowLap={data.currentLap}
                      flowVersion={currentVersion.version}
                      editable={supportDocumentsEditable}
                      isFlowApproved={isFlowApproved}
                    />
                  )}
                </div>
              )}
            </CardBody>
            {data.actions.includes('RESTART') &&
            !(
              data.isOperationApproved &&
              data.isLastStep &&
              (flowType === 'ESRS' || flowType === 'INITIAL_ESRS')
            ) ? (
              <div className="d-flex justify-content-end">
                <Button
                  className="btn-primary"
                  text={t(`flowRestart.${flowType}`)}
                  type="button"
                  onClick={toRestartConfirm}
                />
              </div>
            ) : (
              (canFeedback ||
                data.canFeedback ||
                data.mustAddReviewer ||
                data.isOperationApproved ||
                currentVersion.feedbacks.length > 0) && (
                <CardBody>
                  {(canFeedback || data.canFeedback) && (
                    <FlowFeedbackProvider
                      title={`${
                        data.canFeedback ? t('commentsProvideTitle') : t('feedbackProvideTitle')
                      }:`}
                      options={indications}
                      canStepForward={canStepForward}
                      feedbackToSpe={data.feedbackToTheSpe}
                      isFinalReview={isFinalReview}
                      state={{ ...feedback }}
                      onChange={(val) => setFeedback({ ...val })}
                      saveFeedback={saveFeedback}
                      speCanFeedback={speCanFeedback}
                      currentStep={data.currentStep}
                      isStepRevise={data.isStepRevise}
                    >
                      <div className="flow-actions d-flex justify-content-end">
                        {data.mustAddReviewer && canEdit && (
                          <Button
                            className="btn-secondary"
                            icon="user-plus"
                            text="Add Reviewer(s)"
                            type="button"
                            onClick={() => setShowInviteReviewersModal(true)}
                          />
                        )}
                        {renderButtonActions()}
                      </div>
                    </FlowFeedbackProvider>
                  )}
                  <FlowFeedbackTable
                    feedbacks={currentVersion.feedbacks}
                    title={t('flowFeedbackTableTitle', {
                      flowType: flowTypeName,
                      version: `${currentVersion.version.toString()}`,
                    })}
                  />
                  <hr className="mt-0" />
                  {!canFeedback && !data.canFeedback && (
                    <div className="flow-actions d-flex justify-content-end">
                      {data.mustAddReviewer && canEdit && (
                        <Button
                          className="btn-secondary"
                          icon="user-plus"
                          text="Add Reviewer(s)"
                          type="button"
                          onClick={() => setShowInviteReviewersModal(true)}
                        />
                      )}
                      {renderButtonActions()}
                    </div>
                  )}
                  {data.isOperationApproved && data.isLastStep && flowType === 'ESRS' && (
                    <div className="notification-wrapper-100 d-flex justify-content-center mb-3">
                      <BasicNotification
                        title={t('warningFlowNoEditableTitle', { flowType: flowTypeName })}
                        message={t('warningFlowNoEditableMessage', {
                          dateApp: customDateStr(data.operationApprovalDate, 'dd/MM/yyy'),
                          flowType: flowTypeName,
                        })}
                        color="warning"
                        icon="exclamation-triangle"
                      />
                    </div>
                  )}
                  {data.isOperationApproved && data.isLastStep && flowType === 'INITIAL_ESRS' && (
                    <div className="notification-wrapper-100 d-flex justify-content-center mb-3">
                      <BasicNotification
                        title={t('warningFlowNoEditableTitle', { flowType: flowTypeName })}
                        message={t('warningInitialEsrsNoEditableMessage', {
                          dateApp: customDateStr(data.operationApprovalDate, 'dd/MM/yyy'),
                          flowType: flowTypeName,
                        })}
                        color="warning"
                        icon="exclamation-triangle"
                      />
                    </div>
                  )}
                </CardBody>
              )
            )}
          </Card>
          {previousVersions && previousVersions.length > 0 && (
            <>
              <h4>{t('history')}</h4>
              <div className="ml-3">
                <Button
                  className="btn-tertiary pl-1"
                  iconRight={chevron}
                  text={t('viewPreviousVersions')}
                  type="button"
                  onClick={() => setShowingVersions((current) => !current)}
                />
                {showingVersions && (
                  <FlowVersionsTable
                    versionsData={data.content}
                    handleDownloadDoc={downloadDoc}
                    flowType={flowType}
                    flowTypeName={flowTypeName}
                    commentsCount={data.commentCount}
                    flowId={data.id}
                    downloadEsap={downloadEsap}
                  />
                )}
              </div>
            </>
          )}
          {canStepForward &&
            data.canAddReviewer &&
            (user.roleCode !== Role.Specialist || inSuperUserMode()) && (
              <ConfirmModal
                title={t('sendNextReview')}
                buttonText={t('sendNextReview')}
                visible={showNextVersionModal}
                setVisible={() => {
                  setShowNextVersionModal(false);
                  setNextReviewState({ members: [] });
                }}
                action={canFeedback ? provideFeedback : sendForNextReview}
                size="md"
              >
                <p style={{ color: '#6d6d6d' }}>
                  {canFeedback
                    ? t('sendNextReviewTextGH')
                    : t('sendNextReviewText', { groupHead: data.groupHeadName })}
                </p>
                <AutocompleteChips<User>
                  text={
                    <>
                      {t('addReviewers')}: <span>(Optional)</span>
                    </>
                  }
                  labelKey="fullName"
                  htmlId="egs-members-typeahead"
                  list={members}
                  placeholder="Ex. Matt Damon"
                  onSearch={handleMembersSearch}
                  onChange={handleMembersChange}
                  loading={loadingMembers}
                  render={(option: User) => <div key={option.email}>{option.fullName}</div>}
                  className="label_autocomplete"
                  multiple
                />
              </ConfirmModal>
            )}
          {data.mustAddSPR && (
            <CustomModal
              title={flowTypeName}
              visible={showSprModal}
              setVisible={setShowSprModal}
              customClass="modal-dialog--header-task"
            >
              <SprForm
                handleSubmit={(values: SprFormData) => {
                  const {
                    supervisionDateFrom: startDate,
                    supervisionDateTo: endDate,
                    rating,
                    rationale,
                    missionId,
                  } = values;

                  if (startDate && endDate && rating && rationale) {
                    sendForNextReview(
                      { startDate, endDate, rating, rationale, missionId },
                      createEsapRationale
                    );
                  }
                  setShowSprModal(false);
                }}
                handleCancel={() => {
                  setShowSprModal(false);
                }}
                formState={{
                  supervisionDateFrom: spr && spr.startDate ? new Date(spr.startDate) : null,
                  supervisionDateTo: spr && spr.endDate ? new Date(spr.endDate) : null,
                  rating: docSprRationale ? docSprRationale.rating : '',
                  rationale: docSprRationale ? docSprRationale.rationale : '',
                  fileName: data.documentName,
                  missions: SprMissions,
                  missionId: spr ? spr.missionId : null,
                  flowType: flowType.toString(),
                }}
              />
            </CustomModal>
          )}
          {isSRESPFFinalReviewByGH && (
            <CustomModal
              title={flowTypeName}
              visible={showFinalReviewModal}
              setVisible={setShowFinalReviewModal}
              customClass="modal-dialog--header-task"
            >
              <SprForm
                handleSubmit={(values: SprFormData) => {
                  const {
                    supervisionDateFrom: startDate,
                    supervisionDateTo: endDate,
                    rating,
                    rationale,
                    missionId,
                  } = values;

                  if (startDate && endDate && rating && rationale) {
                    service(
                      () =>
                        operFlowService.provideFeedback({
                          operationFlowId: data.id,
                          fromStep: data.currentStep,
                          members: nextReviewState.members,
                          flowSpr: { startDate, endDate, rating, rationale, missionId },
                          ...feedback,
                        }),
                      {
                        callback: () => {
                          setShowFinalReviewModal(false);
                          notify(flowTypeName, getMessage(), 'success');
                          setFeedback({
                            indicationCode: '',
                            comment: '',
                          });
                          reload();
                          if (isFinalReview && feedback.indicationCode !== data.indicators[0]) {
                            // is approving
                            reloadOperation();
                          }
                        },
                        loader: setLoading,
                      }
                    );
                  }
                }}
                handleCancel={() => {
                  setShowFinalReviewModal(false);
                }}
                formState={{
                  supervisionDateFrom: spr && spr.startDate ? new Date(spr.startDate) : null,
                  supervisionDateTo: spr && spr.endDate ? new Date(spr.endDate) : null,
                  rating: docSprRationale ? docSprRationale.rating : '',
                  rationale: docSprRationale ? docSprRationale.rationale : '',
                  fileName: data.documentName,
                  missions: SprMissions,
                  missionId: spr ? spr.missionId : null,
                  flowType: flowType.toString(),
                }}
              />
            </CustomModal>
          )}
          {data.mustAddReviewer && (
            <CustomModal
              title={t('Add Reviewer(s)')}
              visible={showInviteReviewersModal}
              setVisible={setShowInviteReviewersModal}
              customClass="modal-dialog--header-task"
            >
              <InviteReviewersForm
                handleCancel={() => {
                  setShowInviteReviewersModal(false);
                }}
                handleMembersSearch={handleMembersSearch}
                handleSubmit={(values: InviteReviewersFormData) => {
                  const { members: selected } = values;
                  const formattedEgsMembers = selected.map((u) => u.email);
                  inviteReviewers(formattedEgsMembers);
                  setShowInviteReviewersModal(false);
                }}
                loadingMembers={loadingMembers}
                members={members}
                key="sa"
                formState={{ members: [] }}
                isCof={isCof}
              />
            </CustomModal>
          )}
          {canEdit && (
            <ConfirmModal
              visible={showUploadModal}
              setVisible={() => {
                setFormData(null);
                setShowUploadModal(false);
              }}
              title="Upload New Document"
              action={upload}
              buttonText="Accept"
            >
              {t('confirmFlowUpload')}
            </ConfirmModal>
          )}
          {canRestartFlow && (
            <ConfirmModal
              visible={showRestartModal}
              setVisible={() => {
                setFormData(null);
                setShowRestartModal(false);
              }}
              title={t(`flowRestart.${flowType}`)}
              action={restart}
              buttonText="Accept"
              actionDisabled={maxRelated()}
            >
              {t('confirmFlowRestart')}
              <FlowRelatedSelection
                setRelatedOperationIds={(values) => setRelatedIds(values)}
                flowType={flowType}
              />
            </ConfirmModal>
          )}
          {data.otherPendingFeedbacks.length > 0 && (
            <ConfirmModal
              visible={showPendingFeedbackModal}
              setVisible={() => {
                setShowPendingFeedbackModal(false);
              }}
              title={t('flowPendingFeedbacksWarning')}
              action={() =>
                data.canAddReviewer ? setShowNextVersionModal(true) : provideFeedback()
              }
              buttonText={t('proceed')}
              customClass="wide-modal"
            >
              <div className="history-content flow-history mb-0 table-responsive">
                <Table>
                  <thead>
                    <tr>
                      <th>{t('version')}</th>
                      <th>{t('savedBy')}</th>
                      <th>{t('date')}</th>
                      <th>{t('feedback')}</th>
                    </tr>
                  </thead>
                  <tbody>{renderPendingFeedbacks()}</tbody>
                </Table>
              </div>
            </ConfirmModal>
          )}
          {showBtnReviewers && (
            <Modal
              visible={showReviewersModal}
              setVisible={setShowReviewersModal}
              title={t('responsibles')}
              actions={[{ text: 'Close', action: () => setShowReviewersModal(false) }]}
            >
              <ReviewersTable
                data={data.content[0].reviewers}
                operFlowService={operFlowService}
                reloadReviewers={reload}
              />
              {canEdit && data.canAddReviewerOnCurrent && (
                <div className="">
                  <Button
                    className="btn-tertiary"
                    iconRight={chevronReviewers}
                    text={t('addReviewers') + t(' : ') + t('(Optional)')}
                    type="button"
                    style={{ padding: '15px 0px 0px 0px' }}
                    onClick={() => setShowingAddReviewers((current) => !current)}
                  />
                  {showingAddReviewers && members && (
                    <>
                      <AutocompleteChips<User>
                        labelKey="fullName"
                        htmlId="egs-members-typeahead"
                        list={members}
                        placeholder="Ex. Matt Damon"
                        onSearch={handleMembersSearch}
                        onChange={(selectedMembers: User[]) => {
                          setMembersForAdd(selectedMembers);
                        }}
                        selected={membersForAdd}
                        loading={loadingMembers}
                        render={(option: User) => <div key={option.email}>{option.fullName}</div>}
                        className="label_autocomplete"
                        multiple
                      />
                      <br />
                      <div className="flow-actions d-flex justify-content-end">
                        <Button
                          key="btnAddReviewer"
                          className="btn-primary"
                          text={t('addReviewer')}
                          type="button"
                          onClick={() => inviteReviewers(membersForAdd.map((x) => x.email))}
                          disabled={membersForAdd.length === 0}
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
            </Modal>
          )}
          {flowType === 'ESRS' && (
            <>
              {/* ESRS V1 */}
              <ConfirmModal
                visible={showEsapV1NoContentModal}
                setVisible={() => setShowEsapV1NoContentModal(false)}
                action={() => (finalEsrs ? provideFeedback() : sendForNextReview(undefined, false))}
                buttonText="OK"
              >
                {t('confirmEsapV1NoContentCreationMessage')}
                <br />
                {t('confirmProceed')}
              </ConfirmModal>
              <ConfirmModal
                visible={showEsapV1ContentModal}
                setVisible={() => setShowEsapV1ContentModal(false)}
                action={() => (finalEsrs ? provideFeedback() : sendForNextReview(undefined, true))}
                buttonText="OK"
              >
                {t('confirmEsapV1ContentCreationMessage')}
                <br />
                {t('confirmProceed')}
              </ConfirmModal>
              {/* ESRS > V1 not Esap Previous */}
              <ConfirmModal
                visible={showNoEsapPreviousEsrsContentModal}
                setVisible={() => setShowNoEsapPreviousEsrsContentModal(false)}
                action={() => (finalEsrs ? provideFeedback() : sendForNextReview(undefined, true))}
                buttonText="OK"
              >
                {t('confirmNoEsapPreviousEsrsContentCreationMessage')}
                <br />
                {t('confirmProceed')}
                <br />
                <br />
                <span className="warning-modal">{t('warningEsapCreation')}</span>
              </ConfirmModal>
              <ConfirmModal
                visible={showNoEsapPreviousEsrsNoContentModal}
                setVisible={() => setShowNoEsapPreviousEsrsNoContentModal(false)}
                action={() => (finalEsrs ? provideFeedback() : sendForNextReview(undefined, false))}
                buttonText="OK"
              >
                {t('confirmNoEsapPreviousEsrsNoContentCreationMessage')}
                <br />
                {t('confirmProceed')}
                <br />
                <br />
                <span className="warning-modal">{t('warningEsapCreation')}</span>
              </ConfirmModal>
              {/* ESRS > V1 Esap Previous */}
              <ConfirmModal
                visible={showEsapPreviousEsrsNoModified}
                setVisible={() => setShowEsapPreviousEsrsNoModified(false)}
                action={() => (finalEsrs ? provideFeedback() : sendForNextReview(undefined, false))}
                buttonText="OK"
              >
                {t('confirmEsapPreviousEsrsNoModifiedCreationMessage')}
                <br />
                {t('confirmProceed')}
                <br />
                <br />
                <span className="warning-modal">{t('warningEsapCreation')}</span>
              </ConfirmModal>
              <ConfirmModal
                visible={showEsapPreviousEsrsModified}
                setVisible={() => setShowEsapPreviousEsrsModified(false)}
                action={() => (finalEsrs ? provideFeedback() : sendForNextReview(undefined, true))}
                buttonText="OK"
              >
                {t('confirmEsapPreviousEsrsModifiedCreationMessage')}
                <br />
                {t('confirmProceed')}
                <br />
                <br />
                <span className="warning-modal">{t('warningEsapCreation')}</span>
              </ConfirmModal>
              <ConfirmModal
                visible={showEsapPreviousEsrsNoContent}
                setVisible={() => {
                  setShowEsapPreviousEsrsNoContent(false);
                }}
                action={() => (finalEsrs ? provideFeedback() : sendForNextReview(undefined, false))}
                buttonText="OK"
              >
                {t('confirmEsapPreviousEsrsNoContentCreationMessage')}
                <br />
                {t('confirmProceed')}
                <br />
                <br />
                <span className="warning-modal">{t('warningEsapCreation')}</span>
              </ConfirmModal>
            </>
          )}
          {flowType === 'SR_ESPF' && (
            <>
              {/* SR - Esap Previous */}
              <ConfirmModal
                visible={showEsapPreviousSRNoModified}
                setVisible={() => {
                  setShowEsapPreviousSRNoModified(false);
                }}
                action={() => sendForNextReviewSREspf(undefined, false)}
                buttonText="OK"
              >
                {t('confirmEsapPreviousSRNoModifiedCreationMessage')}
                <br />
                {t('confirmProceed')}
                <br />
                <br />
                <span className="warning-modal">{t('warningEsapCreationSR')}</span>
              </ConfirmModal>
              <ConfirmModal
                visible={showEsapPreviousSRModified}
                setVisible={() => {
                  setShowEsapPreviousSRModified(false);
                }}
                action={() => sendForNextReviewSREspf(undefined, true)}
                buttonText="OK"
              >
                {t('confirmEsapPreviousSRModifiedCreationMessage')}
                <br />
                {t('confirmProceed')}
                <br />
                <br />
                <span className="warning-modal">{t('warningEsapCreationSR')}</span>
              </ConfirmModal>
              <Modal
                visible={showEsapPreviousSRDeleted}
                setVisible={() => {
                  setShowEsapPreviousSRDeleted(false);
                }}
                actions={[{ text: 'Close', action: () => setShowEsapPreviousSRDeleted(false) }]}
              >
                {t('confirmEsapPreviousSRDeletedCreationMessage')}
                <br />
                <br />
                <span className="warning-modal">{t('warningEsapCreationSR')}</span>
              </Modal>

              {/* SR - NO Esap Previous */}
              <ConfirmModal
                visible={showNoEsapPreviousSRContentModal}
                setVisible={() => {
                  setShowNoEsapPreviousSRContentModal(false);
                }}
                action={() => sendForNextReviewSREspf(undefined, true)}
                buttonText="OK"
              >
                {t('confirmNoEsapPreviousSRContentCreationMessage')}
                <br />
                {t('confirmProceed')}
              </ConfirmModal>
              <ConfirmModal
                visible={showNoEsapPreviousSRNoContentModal}
                setVisible={() => {
                  setShowNoEsapPreviousSRNoContentModal(false);
                }}
                action={() => sendForNextReviewSREspf(undefined, false)}
                buttonText="OK"
              >
                {t('confirmNoEsapPreviousSRNoContentCreationMessage')}
                <br />
                {t('confirmProceed')}
              </ConfirmModal>
            </>
          )}
        </>
      )}
    </SpinnerArea>
  );
};

export default FlowMain;
